// "[verify]" markers in a generated note: the note generator puts one right
// after a vital sign, dose, frequency, allergy or result it could not hear
// clearly (packages/processing PROMPT_VERIFY_RULES), keeping its best
// reading. The workspace shows each one as an amber "verify" chip and a
// "Needs review · N" count.
//
// The marker never leaves ROSC (clinical lead, 22/10): Copy, section copy,
// Export and creating a patient summary or referral letter first open a
// review step (workspace/VerifyReview.tsx) where the clinician confirms each
// value (the marker is removed and the note saved) or edits it. Nothing is
// copied or exported while a marker remains.

export const VERIFY_MARKER = "[verify]";

// Pre-chart visits: the final note writes "(pre-chart)" before the marker
// of an item only the pre-chart gave (the visit never mentioned it). The tag
// goes with the marker when the item is confirmed.
export const PRE_CHART_TAG = "(pre-chart)";
const PRE_CHART_TAG_SOURCE = String.raw`[ \t]*\(pre-chart\)`;

// Also matches the escaped form "\[verify\]" that the editor saves (Turndown
// escapes square brackets), in any letter case.
const MARKER_SOURCE = String.raw`\\?\[verify\\?\]`;

export const verifyMarkerPattern = (): RegExp =>
  new RegExp(MARKER_SOURCE, "gi");

export const countVerifyMarkers = (text: string | null | undefined): number =>
  text ? (text.match(verifyMarkerPattern()) ?? []).length : 0;

// The note without its n-th marker (0-based), and the space before it: the
// clinician confirmed that value. Unchanged when there is no such marker.
export const removeVerifyMarker = (text: string, n: number): string => {
  let seen = 0;
  for (const match of text.matchAll(verifyMarkerPattern())) {
    if (seen === n) {
      let start = match.index ?? 0;
      while (start > 0 && /[ \t]/.test(text[start - 1] ?? "")) start -= 1;
      const tag = new RegExp(`${PRE_CHART_TAG_SOURCE}$`, "i").exec(
        text.slice(0, start),
      );
      if (tag) start = tag.index;
      return (
        text.slice(0, start) + text.slice((match.index ?? 0) + match[0].length)
      );
    }
    seen += 1;
  }
  return text;
};

// Every marker removed, for output that must never carry one. Callers only
// use it once the clinician resolved the markers it applies to.
export const stripVerifyMarkers = (text: string): string =>
  text.replace(
    new RegExp(
      String.raw`(?:${PRE_CHART_TAG_SOURCE})?[ \t]*${MARKER_SOURCE}`,
      "gi",
    ),
    "",
  );

export interface VerifyMarkerContext {
  // 0-based index of the marker in the whole note.
  index: number;
  // Plain text just before the marker (the value it follows) and after it.
  before: string;
  after: string;
}

const BEFORE_CHARS = 72;
const AFTER_CHARS = 36;

const clipStart = (text: string): string => {
  if (text.length <= BEFORE_CHARS) return text;
  const cut = text.slice(-BEFORE_CHARS);
  const space = cut.indexOf(" ");
  return `…${space >= 0 && space < 20 ? cut.slice(space + 1) : cut}`;
};
const clipEnd = (text: string): string => {
  if (text.length <= AFTER_CHARS) return text;
  const cut = text.slice(0, AFTER_CHARS);
  const space = cut.lastIndexOf(" ");
  return `${space > 12 ? cut.slice(0, space) : cut}…`;
};

// Each marker with the words around it, for the review list: the line it is
// on, as plain text, without list bullets or other markers.
export const verifyMarkerContexts = (
  markdown: string,
  plain: (line: string) => string,
): VerifyMarkerContext[] => {
  const found: VerifyMarkerContext[] = [];
  let index = 0;
  for (const line of markdown.split("\n")) {
    const count = countVerifyMarkers(line);
    if (!count) continue;
    const text = plain(line)
      .replace(/^\s*(?:[-*+•]|\d+[.)]|☐)\s+/u, "")
      .trim();
    const parts = text.split(verifyMarkerPattern());
    for (let k = 0; k < count; k += 1) {
      found.push({
        index,
        before: clipStart(
          parts
            .slice(0, k + 1)
            .join("")
            .replace(/\s{2,}/g, " ")
            .trim(),
        ),
        after: clipEnd((parts[k + 1] ?? "").trim()),
      });
      index += 1;
    }
  }
  return found;
};

export interface VerifyChipOptions {
  // Visible chip text ("verify") and its tooltip.
  label: string;
  title: string;
  // Index of this fragment's first marker in the whole note, so a chip in a
  // section maps to the same marker in the editor.
  offset?: number;
  // Chips are buttons (open the editor at the marker) only when the note can
  // be edited; otherwise they are labels.
  interactive?: boolean;
}

// Amber, readable in both themes (same tokens as the speaker and prior-source
// chips). Kept as a literal so Tailwind generates the classes.
export const VERIFY_CHIP_CLASS =
  "verify-chip inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-1.5 align-baseline text-[0.8em] font-medium leading-snug text-amber-950 no-underline dark:border-amber-400/40 dark:bg-amber-400/15 dark:text-amber-100";
const VERIFY_CHIP_INTERACTIVE_CLASS =
  "cursor-pointer hover:bg-amber-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:bg-amber-400/25";

// Replaces each marker in the text of a rendered note (not in code) with a
// chip element. Returns how many it replaced.
export const replaceVerifyMarkers = (
  root: HTMLElement,
  { label, title, offset = 0, interactive = false }: VerifyChipOptions,
): number => {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    if (text.parentElement?.closest("code, pre")) continue;
    if (verifyMarkerPattern().test(text.data)) textNodes.push(text);
  }
  let index = offset;
  for (const text of textNodes) {
    const fragment = doc.createDocumentFragment();
    let last = 0;
    for (const match of text.data.matchAll(verifyMarkerPattern())) {
      const start = match.index ?? 0;
      fragment.append(text.data.slice(last, start));
      const chip = doc.createElement(interactive ? "button" : "span");
      if (interactive) chip.setAttribute("type", "button");
      chip.className = interactive
        ? `${VERIFY_CHIP_CLASS} ${VERIFY_CHIP_INTERACTIVE_CLASS}`
        : VERIFY_CHIP_CLASS;
      chip.setAttribute("data-verify-index", String(index));
      chip.title = title;
      chip.setAttribute("aria-label", `${label}: ${title}`);
      chip.textContent = label;
      fragment.append(chip);
      index += 1;
      last = start + match[0].length;
    }
    fragment.append(text.data.slice(last));
    text.replaceWith(fragment);
  }
  return index - offset;
};

// Selects the n-th marker (0-based) in an editable element, scrolls it into
// view and focuses the element, so the clinician lands on the unclear value.
// Returns false when there is no such marker.
export const selectVerifyMarker = (root: HTMLElement, n: number): boolean => {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let seen = 0;
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    for (const match of text.data.matchAll(verifyMarkerPattern())) {
      if (seen === n) {
        const start = match.index ?? 0;
        const range = doc.createRange();
        range.setStart(text, start);
        range.setEnd(text, start + match[0].length);
        root.focus({ preventScroll: true });
        const selection = doc.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        text.parentElement?.scrollIntoView?.({ block: "center" });
        return true;
      }
      seen += 1;
    }
  }
  return false;
};
